import { __awaiter } from "tslib";
import { Request, Tools, Logger, MESSAGES, SparqlTools, LoadingIcon } from '../sgvizler';
/**
 *
 * @class sgvizler.
 * @memberof sgvizler
 */
export var CONTAINER_STATE;
(function (CONTAINER_STATE) {
    CONTAINER_STATE[CONTAINER_STATE["FAILED"] = 0] = "FAILED";
    CONTAINER_STATE[CONTAINER_STATE["LOADED"] = 1] = "LOADED";
    CONTAINER_STATE[CONTAINER_STATE["LOADING"] = 2] = "LOADING";
})(CONTAINER_STATE || (CONTAINER_STATE = {}));
/**
 * Draws charts specified in HTML containers, here we call them
 * "sgvizler-containers".
 *
 * Example of use: The following sgvizler-container will be
 * selected by sgvizler due to the use of designated
 * attributes. The result is a pie chart (draw with
 * `google.visualization.PieChart`) showing the number of instance
 * per class in the endpoint at
 * `http://sws.ifi.uio.no/sparql/ndp`.
 *
 *     <div id="ex1"
 *          data-sgvizler-endpoint="http://sws.ifi.uio.no/sparql/npd"
 *          data-sgvizler-query="SELECT ?class (count(?instance) AS ?noOfInstances)
 *                               WHERE{ ?instance a ?class }
 *                               GROUP BY ?class
 *                               ORDER BY ?class"
 *          data-sgvizler-chart="google.visualization.PieChart"
 *          style="width:800px; height:400px;"></div>
 *
 * These container must have an id attribute (or else sgvizler
 * will not know where to put the chart) and a query attribute (or
 * else the container will be ignored by sgvizler).
 *
 * Dependencies:
 *
 *  - sgvizler.util
 *  - sgvizler.loader
 *  - sgvizler.logger
 *  - sgvizler.Query
 *  - jQuery
 * @class sgvizler.Container
 * @memberof sgvizler
 */
export class Container {
    /**
     * Collects values designated for sgvizler in the given.
     * See also property PREFIX.
     * @param {string} elementID The ID for which the attributes should be collected.
     */
    constructor(elementID) {
        this._lang = 'en';
        this._chartOptions = '';
        this._chartName = '';
        this._loglevel = 0;
        this._id = '';
        // step 1 : read parameters and create the object Query
        // pre-condition
        let element = document.getElementById(elementID);
        if (element === null) {
            throw new Error('elementID unknown : ' + elementID);
        }
        let self = Container;
        this._state = CONTAINER_STATE.LOADING;
        let elmAttrs = element.attributes;
        // read basic parameters
        if (elmAttrs[self.LANG]) {
            this._lang = elmAttrs[self.LANG].value;
        }
        if (elmAttrs[self.LOG_LEVEL_ATTRIBUTE_NAME]) {
            this._loglevel = parseInt(elmAttrs[self.LOG_LEVEL_ATTRIBUTE_NAME].value, 10);
        }
        if (elmAttrs[self.CHART_ATTRIBUTE_NAME]) {
            this._chartName = elmAttrs[self.CHART_ATTRIBUTE_NAME].value;
            // This code will disappear but it's necessary for the migration with the old Sgvizler
            switch (this._chartName) {
                case 'google.visualization.AnnotatedTimeLine':
                case 'google.visualization.Gauge':
                case 'google.visualization.ImageSparkLine':
                case 'google.visualization.MotionChart':
                case 'sgvizler.visualization.D3ForceGraph':
                case 'sgvizler.visualization.DefList"':
                case 'sgvizler.visualization.DraculaGraph':
                case 'sgvizler.visualization.List':
                case 'sgvizler.visualization.Text':
                case 'sgvizler.visualization.MapWKT':
                    {
                        console.warn('Sgvizler2 : ' + this._chartName + ' is deprecated. Please choose another chart.');
                        this._chartName = 'bordercloud.visualization.DataTable';
                        break;
                    }
                case 'sgvizler.visualization.Map':
                    {
                        console.warn('Sgvizler2 : ' + this._chartName + ' is obsolete. Please choose leaflet.visualization.Map or another chart.');
                        this._chartName = 'leaflet.visualization.Map';
                        break;
                    }
                case 'google.visualization.GeoMap':
                    {
                        console.warn('Sgvizler2 : ' + this._chartName + ' is obsolete. Please choose google.visualization.Map or another chart.');
                        this._chartName = 'google.visualization.Map';
                        break;
                    }
                case 'google.visualization.PieChart':
                    {
                        console.warn('Sgvizler2 : ' + this._chartName + ' is obsolete. Please choose google.visualization.Pie or another chart.');
                        this._chartName = 'google.visualization.Pie';
                        break;
                    }
            }
        }
        if (elmAttrs[self.CHART_OPTION_ATTRIBUTE_NAME]) {
            this._chartOptions =
                Tools.decodeHtml(elmAttrs[self.CHART_OPTION_ATTRIBUTE_NAME].value);
        }
        // build request object
        let request = new Request();
        request.container = this;
        this._id = elementID;
        if (elmAttrs[self.QUERY_ATTRIBUTE_NAME]) {
            request.query = Tools.decodeHtml(elmAttrs[self.QUERY_ATTRIBUTE_NAME].value);
        }
        if (elmAttrs[self.ENDPOINT_ATTRIBUTE_NAME]) {
            request.endpoint = Tools.decodeHtml(elmAttrs[self.ENDPOINT_ATTRIBUTE_NAME].value);
        }
        else {
            this._state = CONTAINER_STATE.FAILED;
            Logger.displayFeedback(this, MESSAGES.ERROR_ENDPOINT_FORGOT);
            return;
        }
        if (elmAttrs[self.OUTPUT_FORMAT_ATTRIBUTE_NAME]) {
            request.endpointOutputFormat = SparqlTools.convertString(elmAttrs[self.OUTPUT_FORMAT_ATTRIBUTE_NAME].value);
        }
        if (elmAttrs[self.OUTPUT_METHOD_ATTRIBUTE_NAME]) {
            request.method = elmAttrs[self.OUTPUT_METHOD_ATTRIBUTE_NAME].value;
        }
        if (elmAttrs[self.QUERY_PARAMETER_ATTRIBUTE_NAME]) {
            request.queryParameter = elmAttrs[self.QUERY_PARAMETER_ATTRIBUTE_NAME].value;
        }
        this._request = request;
        // build the chart object
        let chart = Tools.getObjectByPath(this.chartName);
        if (chart === undefined) {
            this._state = CONTAINER_STATE.FAILED;
            Logger.displayFeedback(this, MESSAGES.ERROR_CHART_UNKNOWN, [this.chartName]);
        }
        else {
            chart.container = this;
            // read with and height of container before chart options
            try {
                let element = $('#' + elementID);
                let widthCss = element.css('width');
                let heightCss = element.css('height');
                if (widthCss !== null) {
                    chart.width = widthCss;
                }
                if (heightCss !== null && heightCss !== '0px') {
                    chart.height = heightCss;
                }
            }
            catch (e) {
                // do nothing, unit test not support jquery
            }
            // read options (and replace may be with and height)
            chart.optionsRaw = this._chartOptions;
            this._chart = chart;
        }
        this.saveRefOfContainer();
    }
    /**
     * Save the reference of container
     */
    saveRefOfContainer() {
        let index = -1;
        let len = Container.list.length;
        for (let i = 0; i < len; i++) {
            let dep = Container.list[i];
            if (this.id === Container.list[i].id) {
                //this._dependenciesToLoad.splice(i)
                index = i;
            }
        }
        if (index != -1) {
            Container.list[index] = this;
        }
        else {
            Container.list.push(this);
        }
    }
    /**
     * Draws the sgvizler-containers with the given element id.
     * @method containerDraw
     * @param {string} elementID
     * @param options
     * @returns {Promise<void>}
     */
    static drawWithElementId(elementID, options) {
        return __awaiter(this, void 0, void 0, function* () {
            let container = new Container(elementID);
            // console.log(container)
            container._loadingIcon = new LoadingIcon(container);
            container.loadingIcon.show();
            Logger.log(container, 'drawing id: ' + elementID);
            yield container.draw();
        });
    }
    // noinspection JSValidateJSDoc
    /**
     * Draws all sgvizler-containers on page.
     * @returns {Promise<any>}
     */
    static drawAll() {
        let promisesArray = [];
        let ids = [];
        let iterator = document.evaluate('//div[@' + Container.PREFIX + 'query]/@id', document, null, XPathResult.ANY_TYPE, null);
        let thisNode = iterator.iterateNext();
        while (thisNode) {
            ids.push(thisNode.value);
            thisNode = iterator.iterateNext();
        }
        for (let id of ids) {
            promisesArray.push(Container.drawWithElementId(id));
        }
        return Promise.all(promisesArray);
    }
    static loadDependenciesId(elementID, options) {
        return __awaiter(this, void 0, void 0, function* () {
            let container = new Container(elementID);
            // console.log(container)
            Logger.log(container, 'Load dependencies id: ' + elementID);
            yield container.loadDependencies();
        });
    }
    static loadAllDependencies() {
        let promisesArray = [];
        let ids = [];
        let iterator = document.evaluate('//div[@' + Container.PREFIX + 'query]/@id', document, null, XPathResult.ANY_TYPE, null);
        let thisNode = iterator.iterateNext();
        while (thisNode) {
            ids.push(thisNode.value);
            thisNode = iterator.iterateNext();
        }
        for (let id of ids) {
            promisesArray.push(Container.loadDependenciesId(id));
        }
        return Promise.all(promisesArray);
    }
    /**
     *
     * @param {string} elementID
     * @param {string} endpoint
     * @param {string} query
     * @param {string} chartName
     * @param {string} options
     * @param {string} loglevel
     * @returns {string}
     */
    static create(elementID, endpoint, query, chartName, options, loglevel, output, method, parameter, lang) {
        let element = document.getElementById(elementID);
        if (element === null) {
            throw new Error('elementID unknown : ' + elementID);
        }
        let self = Container;
        let attrQuery = document.createAttribute(self.QUERY_ATTRIBUTE_NAME);
        let attrEndpoint = document.createAttribute(self.ENDPOINT_ATTRIBUTE_NAME);
        let attrChart = document.createAttribute(self.CHART_ATTRIBUTE_NAME);
        // attrQuery.value = Tools.escapeHtml(query)
        attrQuery.value = query;
        attrEndpoint.value = endpoint;
        attrChart.value = chartName;
        element.setAttributeNode(attrQuery);
        element.setAttributeNode(attrEndpoint);
        element.setAttributeNode(attrChart);
        if (options) {
            let attrOptions = document.createAttribute(self.CHART_OPTION_ATTRIBUTE_NAME);
            attrOptions.value = options;
            element.setAttributeNode(attrOptions);
        }
        if (loglevel) {
            let attrLevel = document.createAttribute(self.LOG_LEVEL_ATTRIBUTE_NAME);
            attrLevel.value = loglevel;
            element.setAttributeNode(attrLevel);
        }
        if (output) {
            let attrOutput = document.createAttribute(self.OUTPUT_FORMAT_ATTRIBUTE_NAME);
            attrOutput.value = output;
            element.setAttributeNode(attrOutput);
        }
        if (method) {
            let attrMethod = document.createAttribute(self.OUTPUT_METHOD_ATTRIBUTE_NAME);
            attrMethod.value = method;
            element.setAttributeNode(attrMethod);
        }
        if (parameter) {
            let attrQueryAttribut = document.createAttribute(self.QUERY_PARAMETER_ATTRIBUTE_NAME);
            attrQueryAttribut.value = parameter;
            element.setAttributeNode(attrQueryAttribut);
        }
        if (lang) {
            let attrQueryAttribut = document.createAttribute(self.LANG);
            attrQueryAttribut.value = lang;
            element.setAttributeNode(attrQueryAttribut);
        }
        return element.innerHTML;
    }
    /**
     *
     * @returns {string}
     */
    get id() {
        return this._id;
    }
    /**
     *
     * @returns {string}
     */
    get lang() {
        return this._lang;
    }
    /**
     * Get the name of chart object.
     * @returns {string}
     */
    get chartName() {
        return this._chartName;
    }
    /**
     * Get the loading icon of container.
     * @returns {string}
     */
    get loadingIcon() {
        return this._loadingIcon;
    }
    ///////////////////////////////////// OPTIONS
    /**
     *
     * @returns {string}
     */
    get chartOptions() {
        return this._chartOptions;
    }
    /**
     *
     * @returns {Chart}
     */
    get chart() {
        return this._chart;
    }
    /**
     *
     * @returns {Request}
     */
    get request() {
        return this._request;
    }
    /**
     *
     * @returns {number}
     */
    get loglevel() {
        return this._loglevel;
    }
    /**
     *
     * @returns {Promise<void>}
     */
    draw() {
        return __awaiter(this, void 0, void 0, function* () {
            let sparqlResult;
            if (this._state === CONTAINER_STATE.FAILED) {
                return;
            }
            try {
                sparqlResult = yield this.request.sendRequest();
                // console.log(queryResult)
            }
            catch (error) {
                console.log(error);
                Logger.displayFeedback(this, MESSAGES.ERROR_REQUEST, [error]);
                this._state = CONTAINER_STATE.FAILED;
            }
            if (this._state === CONTAINER_STATE.FAILED) {
                return;
            }
            let sparqlResultI = sparqlResult;
            if (sparqlResultI.head === undefined) {
                console.log(sparqlResultI);
                Logger.displayFeedback(this, MESSAGES.ERROR_CHART, ['ERROR_head_undefined']);
                this._state = CONTAINER_STATE.FAILED;
            }
            try {
                this._chart.loadDependenciesAndDraw(sparqlResultI);
            }
            catch (error) {
                console.log(error);
                Logger.displayFeedback(this, MESSAGES.ERROR_CHART, [error]);
                this._state = CONTAINER_STATE.FAILED;
            }
        });
    }
    loadDependencies() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                yield this._chart.loadDependencies();
            }
            catch (error) {
                console.log(error);
                Logger.displayFeedback(this, MESSAGES.ERROR_DEPENDENCIES, [error]);
                this._state = CONTAINER_STATE.FAILED;
            }
        });
    }
}
Container.list = [];
Container.LANG = 'lang';
Container.PREFIX = 'data-sgvizler-';
Container.QUERY_ATTRIBUTE_NAME = Container.PREFIX + 'query';
Container.ENDPOINT_ATTRIBUTE_NAME = Container.PREFIX + 'endpoint';
Container.OUTPUT_FORMAT_ATTRIBUTE_NAME = Container.PREFIX + 'endpoint-output-format';
Container.QUERY_PARAMETER_ATTRIBUTE_NAME = Container.PREFIX + 'endpoint-query-parameter';
Container.OUTPUT_METHOD_ATTRIBUTE_NAME = Container.PREFIX + 'endpoint-method';
Container.CHART_ATTRIBUTE_NAME = Container.PREFIX + 'chart';
Container.CHART_OPTION_ATTRIBUTE_NAME = Container.PREFIX + 'chart-options';
Container.LOG_LEVEL_ATTRIBUTE_NAME = Container.PREFIX + 'log';
//# sourceMappingURL=data:application/json;base64,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